import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, Camera, FileText, MapPin, X, Calendar } from "lucide-react";
import { Pill, EmptyState } from "@/design/components";
import { formatCardDate, formatDateTime } from "@/design/format";
import { cn } from "@/design/cn";
import { useSeed, useMoney } from "@/domain/hooks";
import type { SpendItem } from "@/domain/types";

/**
 * Evidence grouped by project, each project a collapsible dropdown (like the
 * monitoring page). Opening an item shows a modal with the uploaded documents
 * and photos.
 */
export function EvidenceByProject({ projectId }: { projectId?: string } = {}) {
  const seed = useSeed();
  const money = useMoney();
  const scoped = projectId ? seed.projects.filter((p) => p.id === projectId) : seed.projects;
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>(
    projectId ? { [projectId]: true } : { [seed.projects[0]?.id]: true },
  );
  const [detail, setDetail] = useState<SpendItem | null>(null);

  const itemsByProject = scoped
    .map((p) => {
      const items = seed.spendItems.filter((i) => {
        const req = seed.spendRequests.find((r) => r.id === i.spendRequestId);
        return req?.projectId === p.id;
      });
      return { project: p, items };
    })
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-3">
      {itemsByProject.length === 0 && (
        <EmptyState
          title="No evidence yet on this project"
          body="Evidence appears here once spend is recorded and photographed against an approved ask."
        />
      )}
      {itemsByProject.map(({ project, items }) => {
        const open = openProjects[project.id];
        const evidenced = items.filter((i) => i.evidence.length > 0).length;
        return (
          <div key={project.id} className="overflow-hidden rounded-lg border border-line bg-card">
            <button
              onClick={() => setOpenProjects((s) => ({ ...s, [project.id]: !s[project.id] }))}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-subtle/60"
            >
              <div className="flex items-center gap-3">
                <ChevronDown className={cn("h-4 w-4 text-faint transition-transform", open && "rotate-180")} />
                <span className="font-medium text-ink">{project.name}</span>
                <Pill variant="outline">{items.length} items</Pill>
              </div>
              <span className="text-xs text-faint">{evidenced} of {items.length} evidenced</span>
            </button>
            {open && (
              <ul className="divide-y divide-hairline border-t border-line">
                {items.map((i) => (
                  <li key={i.id}>
                    <button onClick={() => setDetail(i)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-subtle/60">
                      <span className="flex h-10 w-10 items-center justify-center rounded bg-subtle text-faint">
                        {i.evidence[0]?.kind === "image" || i.evidence.length === 0 ? <Camera className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{i.description}</p>
                        <p className="truncate text-xs text-faint">{i.supplier} · {i.evidence.length} file{i.evidence.length === 1 ? "" : "s"}</p>
                      </div>
                      <span className="text-sm tabular-nums text-mute">{money(i.amount)}</span>
                      <Pill variant={i.evidence.length > 0 ? "success" : "warn"}>{i.evidence.length > 0 ? "Evidenced" : "Pending"}</Pill>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      <EvidenceModal item={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function EvidenceModal({ item, onClose }: { item: SpendItem | null; onClose: () => void }) {
  const money = useMoney();
  return (
    <Dialog.Root open={!!item} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[640px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-line bg-card shadow-overlay animate-fade-in">
          {item && (
            <>
              <div className="flex items-start justify-between border-b border-line px-5 py-4">
                <div>
                  <Dialog.Title className="text-base font-semibold text-ink">{item.description}</Dialog.Title>
                  <p className="mt-0.5 text-sm text-mute">{item.supplier} · {money(item.amount)}</p>
                </div>
                <Dialog.Close className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-faint hover:bg-subtle hover:text-ink" aria-label="Close"><X className="h-4 w-4" /></Dialog.Close>
              </div>
              <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
                {/* Uploaded files */}
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Uploaded evidence ({item.evidence.length})</h4>
                  {item.evidence.length === 0 ? (
                    <p className="rounded-md bg-subtle px-3 py-2 text-sm text-faint">No files uploaded yet. This item is awaiting evidence.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {item.evidence.map((f) => (
                        <div key={f.id} className="overflow-hidden rounded-lg border border-line">
                          <div className="flex aspect-[4/3] items-center justify-center bg-subtle text-faint">
                            {f.kind === "image" ? <Camera className="h-7 w-7" /> : <FileText className="h-7 w-7" />}
                          </div>
                          <div className="px-3 py-2">
                            <p className="truncate text-xs font-medium text-ink">{f.filename}</p>
                            <p className="text-[11px] text-faint">{Math.round(f.sizeBytes / 1024)} KB · {formatCardDate(f.uploadedAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Metadata */}
                <dl className="space-y-1.5 rounded-md bg-subtle/60 p-3 text-sm">
                  {item.note && <Meta icon={<FileText className="h-3.5 w-3.5" />} label="Note" value={item.note} />}
                  {item.geoTag && <Meta icon={<MapPin className="h-3.5 w-3.5" />} label="Geo-tag" value={`${item.geoTag.lat.toFixed(4)}° N, ${item.geoTag.lng.toFixed(4)}° E`} />}
                  <Meta icon={<Calendar className="h-3.5 w-3.5" />} label="Committed" value={formatDateTime(item.committedAt)} />
                </dl>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Meta({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-faint">{icon}</span>
      <span className="text-faint">{label}:</span>
      <span className="flex-1 text-ink">{value}</span>
    </div>
  );
}
